import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, RefreshCw, Search, Trash2 } from 'lucide-react';
import type { RecommendationToken } from '@sleed/bizcommerce-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { DateInput } from '~/components/date-input';
import { SegmentedControl } from '~/components/segmented-control';
import { ProductThumb } from '~/components/catalog/bundles/product-combobox';
import { formatDateTime } from '~/lib/format-date';
import { serverMessage } from '~/lib/api/server-error';
import { recommenderName, type RecommendationKind } from '~/lib/reviews/reviews';
import { useRecommendationMutations, useRecommendationsList } from '~/hooks/use-recommendations';

const PAGE_SIZE = 25;

/**
 * Who recommends which product — customers' "I recommend it" from the storefront and contributors' recommendations
 * (legacy recommendation grids). Read + delete; a customer manages their own from the product page.
 */
export function RecommendationsGrid() {
	const { t } = useTranslation();
	const [kind, setKind] = useState<RecommendationKind>('customer');
	const [page, setPage] = useState(0);
	const [searchInput, setSearchInput] = useState('');
	const [search, setSearch] = useState('');
	const [dateFrom, setDateFrom] = useState<string | null>(null);
	const [dateTo, setDateTo] = useState<string | null>(null);
	const [deleteRow, setDeleteRow] = useState<RecommendationToken | null>(null);

	useEffect(() => {
		const id = setTimeout(() => {
			setSearch(searchInput);
			setPage(0);
		}, 350);
		return () => clearTimeout(id);
	}, [searchInput]);

	const params = useMemo(() => ({ skip: page * PAGE_SIZE, take: PAGE_SIZE, search, dateFrom, dateTo }), [page, search, dateFrom, dateTo]);
	const { data, isLoading, isError, isFetching, refetch } = useRecommendationsList(kind, params);
	const { remove } = useRecommendationMutations();

	const rows = data?.items ?? [];
	const total = data?.totalCount ?? 0;
	const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
	const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
	const to = Math.min((page + 1) * PAGE_SIZE, total);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<div className="flex flex-col gap-3">
			<div className="flex flex-wrap items-end gap-3 rounded-md border border-border bg-surface p-3">
				<SegmentedControl<RecommendationKind>
					value={kind}
					onValueChange={next => {
						setKind(next);
						setPage(0);
					}}
					options={[
						{ value: 'customer', label: t('catalog.reviews.recommendations.customer') },
						{ value: 'contributor', label: t('catalog.reviews.recommendations.contributor') }
					]}
				/>
				<div className="flex flex-col gap-1.5">
					<label className="text-xs font-medium text-foreground-subtle">{t('common.search')}</label>
					<div className="relative w-60">
						<Search size={16} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-foreground-subtle" />
						<Input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder={t('common.search')} className="pl-8" />
					</div>
				</div>
				<div className="flex flex-col gap-1.5">
					<label className="text-xs font-medium text-foreground-subtle">{t('catalog.reviews.dateFrom')}</label>
					<DateInput
						value={dateFrom ? new Date(dateFrom) : null}
						onChange={d => {
							setDateFrom(d ? d.toISOString() : null);
							setPage(0);
						}}
						placeholder="—"
					/>
				</div>
				<div className="flex flex-col gap-1.5">
					<label className="text-xs font-medium text-foreground-subtle">{t('catalog.reviews.dateTo')}</label>
					<DateInput
						value={dateTo ? new Date(dateTo) : null}
						onChange={d => {
							setDateTo(d ? d.toISOString() : null);
							setPage(0);
						}}
						placeholder="—"
					/>
				</div>
			</div>

			<div className="flex items-center justify-between gap-4 rounded-t-md border border-b-0 border-border bg-elevated px-3 py-2 text-xs text-foreground-muted">
				<span>
					{t('system.grid.showing')}{' '}
					<strong className="font-medium tabular-nums text-foreground">
						{from}–{to}
					</strong>{' '}
					{t('system.grid.of')} <strong className="font-medium tabular-nums text-foreground">{total.toLocaleString('el-GR')}</strong>
				</span>
				<button
					type="button"
					onClick={() => void refetch()}
					title={t('common.refresh')}
					className="rounded-sm p-1.5 text-foreground-muted hover:bg-muted hover:text-foreground">
					<RefreshCw size={13} className={isFetching ? 'animate-spin' : undefined} />
				</button>
			</div>

			<div className="overflow-x-auto rounded-b-md border border-border bg-surface">
				<table className="w-full border-collapse text-left text-sm" style={{ minWidth: 720 }}>
					<thead>
						<tr className="border-b border-border bg-elevated text-[10px] font-semibold tracking-[0.04em] text-foreground-subtle">
							<th className="px-3 py-2">{t('catalog.reviews.columns.product')}</th>
							<th className="px-3 py-2">{t('catalog.reviews.recommendations.from')}</th>
							<th className="px-3 py-2">{t('catalog.reviews.columns.date')}</th>
							<th className="px-3 py-2" />
						</tr>
					</thead>
					<tbody>
						{isError ? (
							<tr>
								<td colSpan={4} className="px-3 py-12 text-center text-foreground-muted">
									{t('common.noResults')}
								</td>
							</tr>
						) : isLoading ? (
							<tr>
								<td colSpan={4} className="p-4">
									<Skeleton className="h-48 w-full rounded-md" />
								</td>
							</tr>
						) : rows.length === 0 ? (
							<tr>
								<td colSpan={4} className="px-3 py-12 text-center text-foreground-muted">
									{t('common.noResults')}
								</td>
							</tr>
						) : (
							rows.map(r => (
								<tr key={r.id} className="border-b border-border-subtle last:border-b-0 hover:bg-elevated">
									<td className="px-3 py-2.5">
										<div className="flex items-center gap-2">
											<ProductThumb src={r.product_entity?.img1} alt={r.product_entity?.nm} size={32} />
											<div className="min-w-0">
												<div className="truncate font-medium text-foreground">
													{r.product_entity?.nm || (r.to_id != null ? `#${r.to_id}` : '—')}
												</div>
												{r.product_entity?.cd && (
													<div className="truncate font-mono text-[11px] text-foreground-muted">{r.product_entity.cd}</div>
												)}
											</div>
										</div>
									</td>
									<td className="px-3 py-2.5 text-foreground">{recommenderName(r) || '—'}</td>
									<td className="px-3 py-2.5 whitespace-nowrap text-xs tabular-nums text-foreground-muted">{formatDateTime(r.cdt)}</td>
									<td className="px-3 py-2.5">
										<div className="flex justify-end">
											<Button variant="ghost" size="icon" aria-label={t('common.delete')} onClick={() => setDeleteRow(r)}>
												<Trash2 size={15} className="text-danger-fg" />
											</Button>
										</div>
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>

			<div className="mt-1 flex items-center justify-end gap-1 rounded-md border border-border bg-surface px-3 py-2 text-xs text-foreground-muted">
				<Button
					variant="outline"
					size="icon"
					type="button"
					disabled={page === 0}
					onClick={() => setPage(0)}
					aria-label={t('system.grid.first')}>
					<ChevronsLeft size={15} />
				</Button>
				<Button
					variant="outline"
					size="icon"
					type="button"
					disabled={page === 0}
					onClick={() => setPage(page - 1)}
					aria-label={t('system.grid.prev')}>
					<ChevronLeft size={15} />
				</Button>
				<span className="px-2">
					{page + 1} / {pageCount}
				</span>
				<Button
					variant="outline"
					size="icon"
					type="button"
					disabled={page >= pageCount - 1}
					onClick={() => setPage(page + 1)}
					aria-label={t('system.grid.next')}>
					<ChevronRight size={15} />
				</Button>
				<Button
					variant="outline"
					size="icon"
					type="button"
					disabled={page >= pageCount - 1}
					onClick={() => setPage(pageCount - 1)}
					aria-label={t('system.grid.last')}>
					<ChevronsRight size={15} />
				</Button>
			</div>

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('catalog.reviews.recommendations.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('catalog.reviews.recommendations.deleteBody')}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
